import { supabase } from './supabaseClient';

export interface EmailClickTrackingRecord {
  id: string;
  project_id: string;
  user_id: string;
  template_id?: string;
  link_type: 'demo_link' | 'project_page' | 'custom_link';
  original_url: string;
  tracking_token: string;
  clicked_at?: string;
  user_agent?: string;
  ip_address?: string;
  referrer?: string;
  metadata?: any;
  created_at: string;
}

export interface EmailTemplateGeneration {
  id: string;
  project_id: string;
  generated_by: string;
  recipient_count: number;
  subject_line: string;
  template_content: string;
  tracking_enabled: boolean;
  generated_at: string;
  metadata?: any;
}

export interface EmailEngagementAnalytics {
  total_templates: number;
  total_recipients: number;
  total_clicks: number;
  unique_clickers: number;
  click_rate: number;
  engagement_data: Array<{
    link_type: string;
    clicks: number;
  }>;
}

export interface TrackingUrlOptions {
  projectId: string;
  userId: string;
  templateId?: string;
  linkType: 'demo_link' | 'project_page' | 'custom_link';
  originalUrl: string;
}

class EmailClickTrackingService {
  /**
   * Generate tracking URLs for email template links
   * Requirements: 5.1, 5.2, 6.5
   */
  async generateTrackingUrl(options: TrackingUrlOptions): Promise<string> {
    try {
      const { data, error } = await supabase.rpc('generate_tracking_url', {
        p_project_id: options.projectId,
        p_user_id: options.userId,
        p_template_id: options.templateId || null,
        p_link_type: options.linkType,
        p_original_url: options.originalUrl
      });

      if (error) {
        console.error('Error generating tracking URL:', error);
        throw new Error(`Failed to generate tracking URL: ${error.message}`);
      }

      return data;
    } catch (error: any) {
      console.error('Error in generateTrackingUrl:', error);
      // Fallback to original URL if tracking fails
      return options.originalUrl;
    }
  }

  /**
   * Generate multiple tracking URLs for a template
   * Requirements: 5.1, 5.2
   */
  async generateTrackingUrlsForTemplate(
    projectId: string,
    templateId: string,
    recipients: Array<{ userId: string; email: string }>,
    links: Array<{ type: 'demo_link' | 'project_page' | 'custom_link'; url: string }>
  ): Promise<Map<string, Map<string, string>>> {
    try {
      const trackingUrls = new Map<string, Map<string, string>>();

      // Generate tracking URLs for each recipient and each link
      for (const recipient of recipients) {
        const userTrackingUrls = new Map<string, string>();
        
        for (const link of links) {
          try {
            const trackingUrl = await this.generateTrackingUrl({
              projectId,
              userId: recipient.userId,
              templateId,
              linkType: link.type,
              originalUrl: link.url
            });
            
            userTrackingUrls.set(link.type, trackingUrl);
          } catch (error) {
            console.error(`Failed to generate tracking URL for user ${recipient.userId}, link ${link.type}:`, error);
            // Fallback to original URL
            userTrackingUrls.set(link.type, link.url);
          }
        }
        
        trackingUrls.set(recipient.userId, userTrackingUrls);
      }

      return trackingUrls;
    } catch (error: any) {
      console.error('Error generating tracking URLs for template:', error);
      throw new Error(`Failed to generate tracking URLs: ${error.message}`);
    }
  }

  /**
   * Record email click event
   * Requirements: 5.1, 5.2, 6.5
   */
  async recordEmailClick(
    trackingToken: string,
    userAgent?: string,
    ipAddress?: string,
    referrer?: string
  ): Promise<{ success: boolean; redirectUrl?: string; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('record_email_click', {
        p_tracking_token: trackingToken,
        p_user_agent: userAgent || null,
        p_ip_address: ipAddress || null,
        p_referrer: referrer || null
      });

      if (error) {
        console.error('Error recording email click:', error);
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: data.success,
        redirectUrl: data.redirect_url,
        error: data.error
      };
    } catch (error: any) {
      console.error('Error in recordEmailClick:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Record email template generation
   * Requirements: 5.1, 5.2
   */
  async recordTemplateGeneration(
    projectId: string,
    recipientCount: number,
    subjectLine: string,
    templateContent: string,
    metadata?: any
  ): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to record template generation');
      }

      const { data, error } = await supabase
        .from('email_template_generations')
        .insert([{
          project_id: projectId,
          generated_by: user.id,
          recipient_count: recipientCount,
          subject_line: subjectLine,
          template_content: templateContent,
          tracking_enabled: true,
          metadata: metadata || {}
        }])
        .select('id')
        .single();

      if (error) {
        console.error('Error recording template generation:', error);
        throw new Error(`Failed to record template generation: ${error.message}`);
      }

      return data.id;
    } catch (error: any) {
      console.error('Error in recordTemplateGeneration:', error);
      throw error;
    }
  }

  /**
   * Get email engagement analytics for a project
   * Requirements: 5.1, 5.2, 5.3
   */
  async getEmailEngagementAnalytics(projectId: string): Promise<EmailEngagementAnalytics> {
    try {
      const { data, error } = await supabase.rpc('get_email_engagement_analytics', {
        p_project_id: projectId
      });

      if (error) {
        console.error('Error fetching email engagement analytics:', error);
        throw new Error(`Failed to fetch analytics: ${error.message}`);
      }

      return data;
    } catch (error: any) {
      console.error('Error in getEmailEngagementAnalytics:', error);
      throw error;
    }
  }

  /**
   * Get click tracking records for a project
   * Requirements: 5.1, 5.2
   */
  async getClickTrackingRecords(projectId: string): Promise<EmailClickTrackingRecord[]> {
    try {
      const { data, error } = await supabase
        .from('email_click_tracking')
        .select(`
          *,
          user:user_id (
            id,
            email,
            username
          )
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching click tracking records:', error);
        throw new Error(`Failed to fetch click tracking records: ${error.message}`);
      }

      return data || [];
    } catch (error: any) {
      console.error('Error in getClickTrackingRecords:', error);
      throw error;
    }
  }

  /**
   * Get template generation history for a project
   * Requirements: 5.1, 5.2
   */
  async getTemplateGenerationHistory(projectId: string): Promise<EmailTemplateGeneration[]> {
    try {
      const { data, error } = await supabase
        .from('email_template_generations')
        .select('*')
        .eq('project_id', projectId)
        .order('generated_at', { ascending: false });

      if (error) {
        console.error('Error fetching template generation history:', error);
        throw new Error(`Failed to fetch template history: ${error.message}`);
      }

      return data || [];
    } catch (error: any) {
      console.error('Error in getTemplateGenerationHistory:', error);
      throw error;
    }
  }

  /**
   * Update contact history with engagement data
   * Requirements: 5.1, 5.2, 6.5
   */
  async updateContactHistoryEngagement(
    projectId: string,
    userId: string,
    engagementData: {
      clicked_through?: boolean;
      engagement_tracked?: boolean;
      metadata?: any;
    }
  ): Promise<void> {
    try {
      const updateData: any = {};
      
      if (engagementData.clicked_through !== undefined) {
        updateData.clicked_through = engagementData.clicked_through;
      }
      
      if (engagementData.engagement_tracked !== undefined) {
        updateData.engagement_tracked = engagementData.engagement_tracked;
      }
      
      if (engagementData.metadata) {
        // Merge with existing metadata
        const { data: existingRecord } = await supabase
          .from('user_contact_history')
          .select('metadata')
          .eq('project_id', projectId)
          .eq('user_id', userId)
          .eq('contact_type', 'email_template')
          .order('contact_date', { ascending: false })
          .limit(1)
          .single();

        const existingMetadata = existingRecord?.metadata || {};
        updateData.metadata = { ...existingMetadata, ...engagementData.metadata };
      }

      const { error } = await supabase
        .from('user_contact_history')
        .update(updateData)
        .eq('project_id', projectId)
        .eq('user_id', userId)
        .eq('contact_type', 'email_template');

      if (error) {
        console.error('Error updating contact history engagement:', error);
        throw new Error(`Failed to update contact history: ${error.message}`);
      }
    } catch (error: any) {
      console.error('Error in updateContactHistoryEngagement:', error);
      throw error;
    }
  }

  /**
   * Create a tracking-enabled email template with personalized tracking URLs
   * Requirements: 5.1, 5.2, 6.5
   */
  async createTrackingEnabledTemplate(
    projectId: string,
    templateContent: string,
    recipients: Array<{ userId: string; email: string }>,
    links: Array<{ type: 'demo_link' | 'project_page' | 'custom_link'; url: string; placeholder: string }>
  ): Promise<Map<string, string>> {
    try {
      // Record template generation
      const templateId = await this.recordTemplateGeneration(
        projectId,
        recipients.length,
        'Email template with tracking',
        templateContent,
        {
          tracking_enabled: true,
          links_count: links.length,
          recipients_count: recipients.length
        }
      );

      // Generate tracking URLs for all recipients and links
      const trackingUrls = await this.generateTrackingUrlsForTemplate(
        projectId,
        templateId,
        recipients,
        links
      );

      // Create personalized templates for each recipient
      const personalizedTemplates = new Map<string, string>();

      for (const recipient of recipients) {
        let personalizedContent = templateContent;
        const userTrackingUrls = trackingUrls.get(recipient.userId);

        if (userTrackingUrls) {
          // Replace link placeholders with tracking URLs
          for (const link of links) {
            const trackingUrl = userTrackingUrls.get(link.type);
            if (trackingUrl) {
              personalizedContent = personalizedContent.replace(
                new RegExp(link.placeholder, 'g'),
                trackingUrl
              );
            }
          }
        }

        personalizedTemplates.set(recipient.email, personalizedContent);
      }

      return personalizedTemplates;
    } catch (error: any) {
      console.error('Error creating tracking-enabled template:', error);
      throw error;
    }
  }
}

export const emailClickTrackingService = new EmailClickTrackingService();